import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  BellIcon, 
  WifiIcon, 
  SignalSlashIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  UserGroupIcon,
  CalendarIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { authFetch } from '../api';

const EmployeeDashboard = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(new Date());
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedToday: 0,
    totalHours: 0
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastSync(new Date());
      fetchData(); // Refresh data when coming back online
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOnline) {
        fetchData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isOnline]);

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Check authentication
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError(t('employeeDashboard.noAuthenticationTokenFound'));
        return;
      }

      // Get user from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Fetch projects and stats
        const [projectsResponse, statsResponse, notificationsResponse] = await Promise.all([
          authFetch('/employee/assigned-projects'),
          authFetch('/employee/dashboard/stats'),
          authFetch('/employee/notifications')
        ]);
        
        // Handle the response structure - data might be wrapped in a 'data' property
        const projectsData = Array.isArray(projectsResponse?.data) ? projectsResponse.data : 
                           Array.isArray(projectsResponse) ? projectsResponse : [];
        setProjects(projectsData);
        
        // Handle the response structure - data might be wrapped in a 'data' property
        const statsData = statsResponse?.data || statsResponse || {
          totalProjects: projectsResponse?.length || 0,
          activeProjects: projectsResponse?.filter(p => p.status !== 'Completed').length || 0,
          completedToday: 0,
          totalHours: 0
        };
        
        setStats(statsData);
        
        console.log('📊 Dashboard Stats Response:', statsResponse);
        console.log('📋 Projects Response:', projectsResponse);
        console.log('🎯 Set Stats State:', statsData);
        console.log('🎯 Set Projects State:', projectsData);
        setNotifications(Array.isArray(notificationsResponse) ? notificationsResponse : []);
        setUnreadCount((Array.isArray(notificationsResponse) ? notificationsResponse : []).filter(n => !n.read).length);
        setLastSync(new Date());
      } else {
        setError(t('employeeDashboard.userDataNotFound'));
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      
      if (error.response?.status === 401) {
        setError(t('employeeDashboard.authenticationFailed'));
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } else {
        setError(t('employeeDashboard.failedToLoadData'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleRefresh = () => {
    fetchData();
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await authFetch(`/employee/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'On Track': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'At Risk': return 'bg-red-100 text-red-800 border-red-200';
      case 'Delayed': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 80) return 'bg-green-500';
    if (percent >= 60) return 'bg-blue-500';
    if (percent >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#173861] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('employeeDashboard.loadingYourDashboard')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex justify-center items-center p-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <div className="space-x-4">
            <button
              onClick={handleRefresh}
              className="bg-[#173861] text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center mx-auto mb-2"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              {t('employeeDashboard.tryAgain')}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              {t('employeeDashboard.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {t('employeeDashboard.welcomeBack')}, {user?.name || t('employeeDashboard.employee')}
              </h1>
              <div className="flex items-center space-x-2">
                {isOnline ? (
                  <WifiIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <SignalSlashIcon className="h-5 w-5 text-red-500" />
                )}
                <span className="text-sm text-gray-500">
                  {isOnline ? t('employeeDashboard.online') : t('employeeDashboard.offline')}
                </span>
                <LanguageSwitcher />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  <BellIcon className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {Array.isArray(notifications) && notifications.length > 0 ? (
                        notifications.map((notification) => (
                          <div
                            key={notification._id}
                            className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => markNotificationAsRead(notification._id)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="h-2 w-2 bg-blue-500 rounded-full mt-2"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  {new Date(notification.createdAt).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          {Array.isArray(notifications) ? t('employeeDashboard.noNotifications') : t('employeeDashboard.loadingNotifications')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-6 w-6 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Last Sync Info */}
          <div className="pb-4">
            <p className="text-sm text-gray-500">
              {t('employeeDashboard.lastUpdated')}: {lastSync.toLocaleTimeString('bg-BG')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('employeeDashboard.totalProjects')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalProjects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('employeeDashboard.activeProjects')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeProjects}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('employeeDashboard.completedToday')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{t('employeeDashboard.totalHours')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHours}</p>
              </div>
            </div>
          </div>
        </div>



        {/* Projects Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('employeeDashboard.yourProjects')}</h2>
          <div className="space-y-4">
            {Array.isArray(projects) && projects.map((project) => (
              <div 
                key={project._id} 
                className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-200"
                onClick={() => navigate(`/employee/project/${project._id}`)}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {project.name}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-3 line-clamp-2">{project.powerCapacity}</p>
                
                {/* {t('employeeDashboard.progress')} Bar */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.progress')}</span>
                    <span className="text-sm text-gray-500">{project.percentCompleted || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project.percentCompleted || 0)}`}
                      style={{ width: `${project.percentCompleted || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Enhanced Project Details */}
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                  {project.location && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t('employeeDashboard.location')}:</span>
                      <span className="text-gray-900">{project.location}</span>
                    </div>
                  )}
                  {project.dueDate && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t('employeeDashboard.dueDate')}:</span>
                      <span className="text-gray-900">{new Date(project.dueDate).toLocaleDateString('bg-BG')}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('employeeDashboard.assignedWorkers')}:</span>
                    <span className="text-gray-900">{project.totalWorkers || 0}{t('employeeDashboard.workers')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('employeeDashboard.dailyTargets')}:</span>
                    <span className="text-gray-900 font-semibold text-green-600">{project.dailyTargets || 0}{t('employeeDashboard.units')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('employeeDashboard.workingHours')}:</span>
                    <span className="text-gray-900">{project.totalWorkingHours || 0}{t('employeeDashboard.hrs')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('employeeDashboard.additionalWork')}:</span>
                    <span className="text-gray-900">{project.totalAdditionalHours || 0}{t('employeeDashboard.hrs')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('employeeDashboard.totalProjectHours')}:</span>
                    <span className="text-gray-900 font-semibold text-blue-600">{project.totalProjectHours || 0}{t('employeeDashboard.hrs')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {(!Array.isArray(projects) || projects.length === 0) && (
            <div className="text-center py-8">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-base mb-2">{t('employeeDashboard.noProjectsAssignedYet')}</p>
              <p className="text-gray-400 text-sm">{t('employeeDashboard.youWillSeeYourAssignedProjects')}</p>
            </div>
          )}
        </div>

        {/* {t('employeeDashboard.quickActions')} */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('employeeDashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/employee/schedule')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-200"
            >
              <CalendarIcon className="h-8 w-8 text-blue-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.schedule')}</span>
            </button>
            <button
              onClick={() => navigate('/employee/time-tracker')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors duration-200"
            >
              <ClockIcon className="h-8 w-8 text-green-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.timeTrack')}</span>
            </button>
            <button
              onClick={() => navigate('/employee/reports')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors duration-200"
            >
              <ChartBarIcon className="h-8 w-8 text-purple-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.reports')}</span>
            </button>
            <button
              onClick={() => navigate('/employee/team-chat')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors duration-200"
            >
              <UserGroupIcon className="h-8 w-8 text-orange-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.teamChat')}</span>
            </button>
          </div>
        </div>

        {/* Special Admin Access Section */}
        {user?.effectivePermissions?.pages && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('employeeDashboard.specialAccess')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {user.effectivePermissions.pages.autopark && (
                <button
                  onClick={() => navigate('/employee/autopark')}
                  className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-200"
                >
                  <svg className="h-8 w-8 text-blue-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.autoPark')}</span>
                </button>
              )}
              {user.effectivePermissions.pages.inventory && (
                <button
                  onClick={() => navigate('/employee/inventory')}
                  className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors duration-200"
                >
                  <svg className="h-8 w-8 text-green-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.inventory')}</span>
                </button>
              )}
              {user.effectivePermissions.pages.contracts && (
                <button
                  onClick={() => navigate('/employee/contracts')}
                  className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors duration-200"
                >
                  <svg className="h-8 w-8 text-purple-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.contracts')}</span>
                </button>
              )}
              {user.effectivePermissions.pages.workers && (
                <button
                  onClick={() => navigate('/employee/workers')}
                  className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors duration-200"
                >
                  <UserGroupIcon className="h-8 w-8 text-orange-500 mb-2" />
                  <span className="text-sm font-medium text-gray-700">{t('employeeDashboard.workers')}</span>
                </button>
              )}
            </div>
            {!Object.values(user.effectivePermissions.pages).some(permission => 
              ['autopark', 'inventory', 'contracts', 'workers'].includes(permission)
            ) && (
              <div className="text-center py-4">
                <p className="text-gray-500 text-sm">{t('employeeDashboard.noSpecialAccess')}</p>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
  );
};

export default EmployeeDashboard;
